import React, { useState, useRef, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Typography,
  SxProps,
  List,
  Stack,
  Paper,
  Avatar,
  createFilterOptions,
} from '@mui/material';
import { useDictionary, useIsDesktop } from 'Hooks';
import { AccordionArrowPurple, PurpleXLite } from 'Images';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';

interface IOption {
  label: string;
  value: string;
  group?: string;
}

interface ICustomNewAutoComplete {
  value?: string;
  setValue: (value: string | null) => void;
  options: IOption[];
  recentOptions: IOption[];
  label: string;
  placeholder?: string;
  style?: SxProps;
  paperComponent?: React.ReactNode;
}

export const CustomNewAutoComplete: React.FC<ICustomNewAutoComplete> = ({
  value,
  setValue,
  options,
  recentOptions,
  label,
  placeholder,
  style,
  paperComponent,
}) => {
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(true);
  const autoC = useRef<HTMLDivElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const dictionary = useDictionary(Dictionary, DICTIONARY.Reimbursements);

  const allOptions: IOption[] = [
    ...recentOptions.map((option) => ({
      ...option,
      group: dictionary.AlternativeMedicine.yourTreatments,
    })),
    ...options.map((option) => ({
      ...option,
      group: dictionary.AlternativeMedicine.allTreatments,
    })),
  ];

  const lastOption = allOptions[allOptions.length - 1];

  const filterOptions = createFilterOptions<IOption>({
    stringify: (option) => option.label,
  });

  const customFilterOptions = (options: IOption[], state: any) => {
    const filtered = filterOptions(options, state);
    if (filtered.length === 0) {
      return [lastOption];
    }
    if (!filtered.includes(lastOption)) {
      filtered.push(lastOption);
    }
    return filtered;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target as Node) &&
        !autoC.current?.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (paperComponent) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [paperComponent]);

  const CustomPaper: React.FC<React.ComponentProps<typeof Paper>> = (props) => {
    return (
      <Paper
        {...props}
        sx={{
          backgroundColor: '#fff',
          boxShadow: 'unset',
          minHeight: '55vh',
          width: '800px',
          position: 'fixed',
          borderRadius: '0',
          '& .MuiAutocomplete-listbox': {
            maxHeight: '55vh',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '100px',
            background: 'linear-gradient(to top, white, transparent)',
            pointerEvents: 'none',
          },
        }}
      />
    );
  };

  return (
    <Autocomplete
      ref={autoC}
      disablePortal
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value ? allOptions.find((option) => option.value === value) : null}
      onChange={(_, newValue: IOption | null) =>
        setValue(newValue ? newValue.value : null)
      }
      options={allOptions}
      filterOptions={customFilterOptions}
      groupBy={(option) => option?.group || ''}
      getOptionLabel={(option) => option.label}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant='outlined'
        />
      )}
      noOptionsText={<Typography>{dictionary.noOptions}</Typography>}
      renderGroup={(params) => (
        <Stack key={params.key}>
          <Typography
            variant={isDesktop ? 'body2' : 'body5'}
            sx={{
              fontWeight: 'bold',
              mt: 3,
              display: 'flex',
              justifyContent: 'start',
            }}
          >
            {params.group}
          </Typography>
          <List component={'ul'} sx={{ pb: '17px' }}>
            <Typography
              variant={isDesktop ? 'body3' : 'body3'}
              sx={{ p: 0, m: 0 }}
            >
              {params.children}
            </Typography>
          </List>
        </Stack>
      )}
      PaperComponent={CustomPaper}
      clearIcon={
        <Avatar
          alt=''
          src={PurpleXLite}
          sx={{
            width: '20px',
            height: '20px',
          }}
        />
      }
      renderOption={(props, option, { selected }) => (
        <li
          {...props}
          style={{
            fontWeight: option === lastOption ? 'bold' : 'normal',
            padding: '6px 0',
          }}
        >
          {option.label}
        </li>
      )}
      popupIcon={
        <Avatar
          alt=''
          src={AccordionArrowPurple}
          sx={{
            width: '32px',
            height: '32px',
            mt: '-4px',
          }}
        />
      }
      sx={{
        width: '100%',
        m: isDesktop ? undefined : '0 auto',
        borderRadius: '8px',
        bgcolor: '#fff',
        '& .MuiOutlinedInput-root': {
          bgcolor: 'primary.shade_50',
          borderBottom: '2px solid #1552E8',
          borderRadius: '6px 6px 0 0',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '& .MuiAutocomplete-clearIndicator': {
          visibility: 'visible',
          opacity: 1,
        },
        maxWidth: '600px',

        '&&&:has(input[dropdown="open"])': {
          borderRadius: '16px 16px 0 0',
        },
        '&&& .MuiOutlinedInput-notchedOutline': {
          borderRadius: '34px',
          borderColor: 'primary.shade_200',
          borderWidth: '1px',
        },
        '&&& .MuiInputBase-root input[dropdown="open"] ~ fieldset': {
          borderColor: 'primary.main',
          borderRadius: '16px 16px 0 0',
          borderBottom: '0',
          borderWidth: '1px',
        },
        '&&& .MuiInputBase-root input[dropdown="closed"] ~ fieldset': {
          border: '1px solid',
          borderColor: 'primary.shade_200',
        },

        '&&& .MuiInputLabel-root': {
          paddingX: '8px',
          paddingY: isDesktop ? '4px' : '0px',
        },
        '&&& .MuiInputLabel-shrink': {
          padding: '0',
        },
        '&&& .MuiOutlinedInput-root': {
          height: isDesktop ? '56px' : '44px',
          borderRadius: '0px',
        },
        '&&& .MuiOutlinedInput-input': {
          padding: '0 15px',
        },
        '& .MuiAutocomplete-popupIndicatorOpen': {
          top: '-3px',
        },
        ...style,
      }}
    />
  );
};

export default CustomNewAutoComplete;
